import OfferRating from 'components/Common/NumberRating/OfferRating'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'LuxKit/Button/TextButton'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import PriceRowFlightPackage from 'LuxKit/PricePoints/PriceRowFlightPackage'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { useBedbankSearchTilePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import { useOfferRatePrice } from 'hooks/Offers/useOfferPrice'
import { useAppSelector } from 'hooks/reduxHooks'
import useBedbankRates from 'hooks/useBedbankRates'
import useFlightPrice from 'hooks/useFlightPrice'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import { selectIsBedbankFlightBookingEnabled } from 'selectors/flightsSelectors'
import styled from 'styled-components'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import WishlistGuard from 'wishlists/components/WishlistGuard'
import OfferTileLoggedInButtons from '../../Support/OfferTileLoggedInButtons'
import HeroBedbankPriceDetails from './HeroBedbankPriceDetails'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'

const DetailContainer = styled(Group)`
  height: 100%;
  padding-top: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  ${mediaQueryUp.desktop} {
    flex: 1;
    padding-block: ${rem(16)} ${rem(32)};
    padding-inline: ${rem(32)};
  }
`

const HeaderActions = styled.div`
  display: grid;
  grid-template: "review save";
`

const SaveButtonWrapper = styled.div`
  grid-area: save;
  justify-self: end;
`

const ReviewWrapper = styled.div`
  grid-area: review;

  &:empty {
    display: none;
  }
`

const MinHeightGroup = styled(Group)`
  min-height: ${rem(300)};
  flex-grow: 1;
`

interface Props {
  offer: App.BedbankOfferSummary | App.BedbankOffer
}

function HeroBedbankOfferDetails(props: Props) {
  const intl = useIntl()
  const { offer } = props
  const [{ hotelOnlyRates }] = useBedbankRates(offer.id)
  const rate = hotelOnlyRates[0]
  const bedbankFlightEnabled = useAppSelector(selectIsBedbankFlightBookingEnabled)
  const flightsEnabled = bedbankFlightEnabled && offer.flights?.airportCode && offer.flights?.flightsEnabled

  const [flightPrice] = useFlightPrice(offer.flights?.airportCode, undefined, undefined, undefined, undefined, rate?.nights ?? offer?.sell?.los)

  const promotions = useBedbankSearchTilePromotions(offer.promotions, rate)
  const promotion = promotions.length > 0 ? promotions[0] : null

  const {
    property,
  } = offer

  const addressArray: Array<string> = []
  const { city, countryName } = property.address
  if (city) addressArray.push(city)
  if (countryName) addressArray.push(countryName)
  const location = property.locationTitleOverride ? property.locationTitleOverride : addressArray.join(', ')

  const {
    totalPrice,
    totalPriceWithFlights,
  } = useOfferRatePrice(offer, rate, { flightPrice }) || {}

  const wall = <OfferTileLoggedInButtons
    align="none"
    fullWidth
  />

  return (
    <DetailContainer direction="vertical" gap={16}>
      <CSSBreakpoint min="desktop">
        <HeaderActions>
          {isOfferRatingDisplayable(offer.property?.rating) && <ReviewWrapper>
            <OfferRating variant="medium" rating={offer.property.rating} />
          </ReviewWrapper>}
          <WishlistGuard>
            <SaveButtonWrapper>
              <BookmarkButton offer={offer} />
            </SaveButtonWrapper>
          </WishlistGuard>
        </HeaderActions>
      </CSSBreakpoint>
      <MinHeightGroup direction="vertical" gap={16} tabletGap={24} verticalAlign="space-between">
        <Group direction="vertical" gap={4} horizontalAlign="start">
          <Caption variant="large" colour="normal-contrast">
            <FormattedMessage
              id="bW08O7ffk0bw2KpuHHvq"
              defaultMessage="{location}<secondaryText> · Hotel</secondaryText>"
              description="Hero bedbank offer location with hotel suffix in the offer listing section"
              values={{
                location,
                secondaryText: (chunks) => <Caption variant="large" colour="light-foreground-alt" as="span">{chunks}</Caption>,
              }}
            />
          </Caption>
          <Heading variant="heading6" lineClamp={3}>{property?.name ?? offer.name}</Heading>
          {isOfferRatingDisplayable(offer.property?.rating) && <CSSBreakpoint max="tablet">
            <OfferRating
              inlineLabel
              variant="xs"
              rating={offer.property.rating}
            />
          </CSSBreakpoint>}
          {promotion && <div>
            <ProductTypeLabel kind="plain" productType="limited_time_special" />
          </div>}
          <BodyText lineClamp={3} variant="medium" colour="normal-contrast">
            {offer.tagline || offer.amenities}
          </BodyText>
        </Group>
        <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing} wall={wall}>
          <Group direction="vertical" gap={8}>
            <Group direction="horizontal" gap={8} horizontalAlign="space-between" verticalAlign="end">
              <HeroBedbankPriceDetails
                rate={rate}
                offer={offer}
              />
              <CSSBreakpoint min="desktop">
                <TextButton kind="primary">
                  {intl.formatMessage({
                    id: 'VihkhHmSXu9vIeeNRtKf',
                    defaultMessage: 'View offer',
                    description: 'Action button to view bedbank offer details on the hero tile',
                  })}
                </TextButton>
              </CSSBreakpoint>
            </Group>
            {flightsEnabled && !!totalPrice && <PriceRowFlightPackage
              product="hotel"
              price={totalPriceWithFlights}
            />}
          </Group>
        </WalledContent>
      </MinHeightGroup>
    </DetailContainer>
  )
}

export default HeroBedbankOfferDetails
